import { Component, Inject, OnInit, ViewEncapsulation } from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatTabChangeEvent,
} from "@angular/material";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { AdminApi } from "@lbsdk/*";
import { FuseSplashScreenService } from "@fuse/services/splash-screen.service";
import { GameSettingsService } from "@app/main/content/game-settings/game-settings.service";
import { HelperService } from "@app/main/core/services/helper.service";
import { prizeButtons } from "../prize-buttons";
import { environment } from "@env/environment";

@Component({
  selector: "app-prize-form",
  templateUrl: "./prize-form.component.html",
  styleUrls: ["./prize-form.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class PrizeFormComponent implements OnInit {
  dialogTitle: string;
  prizeForm: FormGroup;
  action: string;
  s3BucketUrl: string;
  imageError = false;
  images: {
    title: string;
    image: string;
    isStretch: boolean;
    description: string;
    type: string; // 'photo' | 'embed video'
  }[] = [];
  index: number;

  /**
   * Constructor for PrizeFormComponent.
   * @param dialogRef - Reference to the MatDialog for this component.
   * @param data - Data passed to the dialog.
   * @param _fuseSplashScreenService - Service for managing the splash screen.
   * @param helperService - Service for all shared helper functions.
   * @param _gameSettingsService - Service for game settings.
   * @param _adminApi - AdminApi service for making API calls.
   * @param formBuilder - FormBuilder for building reactive forms.
   */
  constructor(
    public dialogRef: MatDialogRef<PrizeFormComponent>,
    @Inject(MAT_DIALOG_DATA) private data: any,
    private _fuseSplashScreenService: FuseSplashScreenService,
    private helperService: HelperService,
    public _gameSettingsService: GameSettingsService,
    private _adminApi: AdminApi,
    private formBuilder: FormBuilder
  ) {

    // Set dialog title
    this.dialogTitle = "Prize Info";

    // Extract index from data
    this.index = data.index;

    // Set action from data
    this.action = data.action;

    // Initialize images array and form based on the provided data and action
    if (data.action === "new") {
      if (
        (data.prizeType === "auto select" || data.prizeType === "top 6") &&
        prizeButtons.map((el) => el.type).includes(data.prizeMode)
      ) {
        let imageCount = 3;
        if (data.prizeType === "auto select") imageCount = 3;        // Initialize images based on the count
        this.images = Array.from({ length: imageCount }, (_, i) => ({
          title: `Image ${i + 1}`,
          image: "",
          isStretch: false,
          description: "",
          type: "photo",
        }));
      } else if (
        data.prizeType === "solo" &&
        prizeButtons.map((el) => el.type).includes(data.prizeMode)
      ) {
        // Initialize six images
        this.images = Array.from({ length: 6 }, (_, i) => ({
          title: `Image ${i + 1}`,
          image: "",
          isStretch: false,
          description: "",
          type: "photo",
        }));
      }
    } else if (data.action === "edit") {
      // Call function to initialize images based on existing prize data
      this.prizeInitialization();
    }

    // Create the prize form
    this.prizeForm = this.createPrizeForm();
  }

  ngOnInit() {
    this.s3BucketUrl = environment.s3BucketUrl;
  }

  /**
   * Define a function to handle image initialization based on the given data.
   * @param index - The index of the image being initialized.
   * @param key - The key used to determine the image properties in the data.prize object.
   */
  initializeImage(index: number, key: string) {
    // Set image properties based on the provided data or use default values
    const prizeData = this.data.prize[key];
    this.images[index] = {
      title: `Image ${index + 1}`,
      image: prizeData ? prizeData.image || "" : "",
      isStretch: prizeData ? prizeData.isStretch || false : false,
      description: prizeData ? prizeData.description || "" : "",
      type: prizeData ? prizeData.type || "photo" : "photo",
    };
  }

  /**
   * Initializes the images array based on the provided data.
   * Sets up the images array according to different conditions.
   * Calls the initializeImage function for each image.
   */
  prizeInitialization() {
    if (this.data.prize) {
      this.images = [];

      if (
        (this.data.prizeType === "auto select" ||
          this.data.prizeType === "top 6") &&
        prizeButtons.map((el) => el.type).includes(this.data.prizeMode)
      ) {
        if (this.data.prizeType === "auto select") {
          // Initialize 3 standard images
          const imageCount = 3;
          for (let i = 0; i < imageCount; i++) {
            this.initializeImage(i, `image${i + 1}`);
          }
        } else this.initializeImage(0, "image1");
      } else if (
        this.data.prizeType === "top 6" &&
        this.data.prizeMode === "auto select mode"
      ) {
        // Initialize a single image
        this.initializeImage(0, "image1");
      } else if (
        this.data.prizeType === "solo" &&
        prizeButtons.map((el) => el.type).includes(this.data.prizeMode)
      ) {
        // Initialize six images
        for (let i = 0; i < 6; i++) {
          this.initializeImage(i, `image${i + 1}`);
        }
      }
    }
  }

  createPrizeForm() {
    return this.formBuilder.group({
      title: [
        this.data.prize && this.data.prize.title ? this.data.prize.title : "",
        Validators.required,
      ],
      description: [
        this.data.prize && this.data.prize.description
          ? this.data.prize.description
          : "",
      ],
      buyNowUrl: [
        this.data.prize && this.data.prize.buyNowUrl
          ? this.data.prize.buyNowUrl
          : "",
        [
          Validators.pattern(
            "^(https?://)" +
              "((([a-zA-Z0-9\\-]+\\.)+[a-zA-Z]{2,})|" +
              "((\\d{1,3}\\.){3}\\d{1,3}))" +
              "(\\:\\d+)?(/[-a-zA-Z0-9@:%._\\+~#=]*)*" +
              "(\\?[;&a-zA-Z0-9%_.~+=-]*)?" +
              "(\\//#[-a-zA-Z0-9_]*)?$"
          ),
        ],
      ],
    });
  }

  toggleIsStretched($event, index) {
    this.images[index].isStretch = $event.checked;
  }

  // TODO: this should be in helper service and should be one functon to upload all the files
  uploadPrizeImage($event, index, compress: boolean = true) {
    if ($event.target.files.length) {
      const file = $event.target.files[0];

      const upload = (blob: Blob) => {
        this._fuseSplashScreenService.show();
        this._gameSettingsService
          .upload(
            blob,
            this._gameSettingsService.prizeImageUploadPath,
            this._adminApi.getCurrentToken().id
          )
          .then((response: any) => {
            this.images[index].image =
              response.result.files["file"][0]["providerResponse"].name;
            this._fuseSplashScreenService.hide();
          })
          .catch((error) => {
            console.error("Error uploading image:", error);
            this._fuseSplashScreenService.hide();
          });
      };

      if (compress) {
        this.helperService
          .compressImage(file)
          .then((result) => {
            upload(result.compressedBlob);
          })
          .catch((error) => {
            console.error("Compression failed, uploading original:", error);
            upload(file); // Fallback to original if compression fails
          });
      } else {
        upload(file); // Upload original if compression is not desired
      }
    }
  }

  /**
   * Returns true if there is at least one image in the images array.
   */
  imagesLength() {
    // For video, 'image' field contains embed code, so just checking 'image' string is sufficient.
    return this.images.filter((item) => item.image !== "").length !== 0;
  }

  prizeValidNAtLeastOneImage() {
    // Check if form is invalid or no images (content) provided
    // Since 'image' holds both URL and Embed Code, checking it covers both cases.
    
    if (
      this.prizeForm.invalid &&
      this.images.filter((item) => item.image !== "").length === 0
    ) {
      this.imageError = true;
      return true;
    }
    if (
      this.prizeForm.valid &&
      this.images.filter((item) => item.image !== "").length === 0
    ) {
      this.imageError = true;
      return true;
    }
    if (
      this.prizeForm.invalid &&
      this.images.filter((item) => item.image !== "").length !== 0
    ) {
      this.imageError = false;
      return true;
    }
    if (
      this.prizeForm.valid &&
      this.images.filter((item) => item.image !== "").length !== 0
    ) {
      this.imageError = false;
      return false;
    }
  }

  getSelectedIndex(): number {
    const prizeConfettiType =
      this._gameSettingsService.prizeConfetti[this.index].type;

    switch (prizeConfettiType) {
      case "color":
        return 0;
      case "emoji":
        return 1;
      case "canvas":
        return 2;
      default:
        return 0; // Default to the first tab (Color Confetti) if the type is not recognized
    }
  }

  /**
   * Handles the tab change event.
   * Sets the flag in the prizeConfetti array based on the selected tab index.
   * @param $event - The tab change event.
   */
  onTabChange(event: MatTabChangeEvent) {
    const selectedIndex = event.index;
    const prizeConfetti = this._gameSettingsService.prizeConfetti[this.index];

    switch (selectedIndex) {
      case 0: // Color Confetti tab
        prizeConfetti.type = "color";
        break;
      case 1: // Emoji Confetti tab
        prizeConfetti.type = "emoji";
        break;
      case 2: // Canvas Confetti tab
        prizeConfetti.type = "canvas";
        break;
      default:
        break;
    }
  }

  onTypeChange(item: any) {
    item.image = "";
  }

  save() {
      this.dialogRef.close({
          title: this.prizeForm.value.title,
          description: this.prizeForm.value.description,
          buyNowUrl: this.prizeForm.value.buyNowUrl,
          images: this.images
      });
  }
}
